import {
  CapacityUsageDto,
  CodeUsageDto,
  SubscriptionUsageResponseDto,
} from '@/shared/openapi';

export const SUB_POP_DISMISSED_KEY = 'sub_pop_dismissed';

/** orgId -> calendar year when the subscription popup was dismissed */
export type SubPopDismissalsByOrg = Record<string, number>;

export interface SubscriptionOverdraftRow {
  label: string;
  usedLabel: string;
  planLabel: string;
  overdraftCostLabel: string;
}

export interface UsageMetricLabels {
  traceability: string;
  extended: string;
  engage: string;
  registeredUsers: string;
  loggedInUsers: string;
  database: string;
  publicStorage: string;
  privateStorage: string;
}

export function getSubPopDismissalYear(): number {
  return new Date().getFullYear();
}

function readSubPopDismissals(): SubPopDismissalsByOrg {
  if (typeof localStorage === 'undefined') {
    return {};
  }

  const raw = localStorage.getItem(SUB_POP_DISMISSED_KEY);
  if (!raw) {
    return {};
  }

  if (raw === 'true') {
    localStorage.removeItem(SUB_POP_DISMISSED_KEY);
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {};
    }
    return parsed as SubPopDismissalsByOrg;
  } catch {
    return {};
  }
}

function writeSubPopDismissals(dismissals: SubPopDismissalsByOrg): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  const currentYear = getSubPopDismissalYear();
  const pruned = Object.fromEntries(
    Object.entries(dismissals).filter(([, year]) => year === currentYear)
  );
  localStorage.setItem(SUB_POP_DISMISSED_KEY, JSON.stringify(pruned));
}

export function isSubPopDismissed(
  organizationId: string | null | undefined
): boolean {
  if (!organizationId) {
    return false;
  }
  const dismissals = readSubPopDismissals();
  return dismissals[organizationId] === getSubPopDismissalYear();
}

export function dismissSubPop(organizationId: string | null | undefined): void {
  if (!organizationId) {
    return;
  }
  const dismissals = readSubPopDismissals();
  dismissals[organizationId] = getSubPopDismissalYear();
  writeSubPopDismissals(dismissals);
}

function formatMb(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
}

/** Preserves small unit prices (e.g. 0.0005) instead of rounding to 0.00. */
export function formatSubscriptionMoney(
  value: number | null | undefined
): string {
  if (value === undefined || value === null) {
    return '-';
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return '-';
  }
  if (num === 0) {
    return '0';
  }
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
    useGrouping: false,
  });
}

function formatCost(amount: number): string {
  return formatSubscriptionMoney(amount);
}

function isCodeOverdraft(block: CodeUsageDto | undefined): boolean {
  if (!block) {
    return false;
  }
  return block.used > block.included || block.overdraft_cost > 0;
}

function isCapacityOverdraft(block: CapacityUsageDto | undefined): boolean {
  if (!block) {
    return false;
  }
  return block.used > block.limit || Number(block.overdraft_cost ?? 0) > 0;
}

function isUserOverdraft(block: CapacityUsageDto | undefined): boolean {
  if (!block) {
    return false;
  }
  return block.used > block.limit;
}

export function hasUsageOverdraft(
  usage: SubscriptionUsageResponseDto
): boolean {
  return (
    collectOverdraftRows(usage, {
      traceability: '',
      extended: '',
      engage: '',
      registeredUsers: '',
      loggedInUsers: '',
      database: '',
      publicStorage: '',
      privateStorage: '',
    }).length > 0
  );
}

export function collectOverdraftRows(
  usage: SubscriptionUsageResponseDto,
  labels: UsageMetricLabels
): SubscriptionOverdraftRow[] {
  const rows: SubscriptionOverdraftRow[] = [];

  const addCode = (label: string, block: CodeUsageDto | undefined) => {
    if (!isCodeOverdraft(block) || !block) {
      return;
    }
    rows.push({
      label,
      usedLabel: String(block.used),
      planLabel: String(block.included),
      overdraftCostLabel: formatCost(block.overdraft_cost),
    });
  };

  addCode(labels.traceability, usage.traceability_codes);
  addCode(labels.extended, usage.extended_codes);
  addCode(labels.engage, usage.engage_codes);

  const addCapacity = (
    label: string,
    block: CapacityUsageDto | undefined,
    formatValue: (bytes: number) => string
  ) => {
    if (!isCapacityOverdraft(block) || !block) {
      return;
    }
    rows.push({
      label,
      usedLabel: formatValue(block.used),
      planLabel: formatValue(block.limit),
      overdraftCostLabel:
        block.overdraft_cost != null ? formatCost(block.overdraft_cost) : '-',
    });
  };

  addCapacity(labels.database, usage.database_size, formatMb);
  addCapacity(labels.publicStorage, usage.public_file_size, formatMb);
  addCapacity(labels.privateStorage, usage.private_file_size, formatMb);

  const addUserCapacity = (
    label: string,
    block: CapacityUsageDto | undefined
  ) => {
    if (!isUserOverdraft(block) || !block) {
      return;
    }
    rows.push({
      label,
      usedLabel: String(block.used),
      planLabel: String(block.limit),
      overdraftCostLabel: '-',
    });
  };

  addUserCapacity(labels.registeredUsers, usage.registered_users);
  addUserCapacity(labels.loggedInUsers, usage.logged_in_users);

  return rows;
}
